import { useMemo, useState } from 'react';
import { useAppState } from '@/ui/AppState';
import { navigate } from '@/ui/router';
import { CategoryChip, EmptyState, FlagBanner, SessionChip } from '@/ui/components/bits';
import { ModelAnswer } from '@/ui/components/ModelAnswer';
import { setNotebookNote, setNotebookPinned } from '@/domain/practice';

export function NotebookScreen() {
  const { notebook, items, sessions, reload, toast } = useAppState();
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [pinnedOnly, setPinnedOnly] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [draftNote, setDraftNote] = useState('');

  const itemById = useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);
  const sessionLabel = useMemo(() => new Map(sessions.map((s) => [s.id, s.shortLabel])), [sessions]);

  const open = useMemo(
    () =>
      notebook
        .filter((entry) => entry.clearedAt === null && itemById.has(entry.itemId))
        .sort((a, b) => Number(b.pinned) - Number(a.pinned) || b.addedAt - a.addedAt),
    [notebook, itemById],
  );

  const categories = useMemo(() => {
    const set = new Set(open.map((e) => itemById.get(e.itemId)!.category));
    return ['all', ...[...set].sort()];
  }, [open, itemById]);

  const shown = open.filter((entry) => {
    if (pinnedOnly && !entry.pinned) return false;
    if (categoryFilter !== 'all' && itemById.get(entry.itemId)!.category !== categoryFilter) return false;
    return true;
  });

  if (open.length === 0) {
    return (
      <div className="stack">
        <h1>Mistake notebook</h1>
        <EmptyState icon="✎" title="Nothing in here">
          Anything you miss lands here automatically, and clears itself once you get it right —
          unless you pin it.
        </EmptyState>
      </div>
    );
  }

  return (
    <div className="stack">
      <div className="spread">
        <h1>Mistake notebook</h1>
        <span className="chip chip--count">{open.length} open</span>
      </div>

      <div className="row">
        <select
          className="select"
          style={{ width: 'auto', flex: 1 }}
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          aria-label="Filter by category"
        >
          {categories.map((c) => (
            <option key={c} value={c}>
              {c === 'all' ? 'All topics' : c}
            </option>
          ))}
        </select>
        <button
          className="btn btn--sm"
          aria-pressed={pinnedOnly}
          onClick={() => setPinnedOnly((p) => !p)}
        >
          {pinnedOnly ? '★ Pinned only' : '☆ Pinned only'}
        </button>
      </div>

      <ul className="list">
        {shown.map((entry) => {
          const item = itemById.get(entry.itemId)!;
          const isOpen = openId === entry.itemId;
          return (
            <li key={entry.itemId} className="card stack">
              <div className="row">
                <CategoryChip category={item.category} />
                <SessionChip label={sessionLabel.get(item.sessionId) ?? item.sessionId} />
                {entry.pinned ? <span className="chip chip--count">★ pinned</span> : null}
              </div>

              <div style={{ whiteSpace: 'pre-wrap' }}>
                {item.type === 'flashcard' ? item.front : item.q}
              </div>

              {isOpen ? (
                <>
                  {item.type === 'flashcard' ? (
                    <>
                      <span className="flashcard__label">Back</span>
                      <div className="sample">{item.back}</div>
                    </>
                  ) : (
                    <>
                      {item.opts ? (
                        <ol style={{ margin: 0, paddingLeft: '1.4em' }}>
                          {item.opts.map((opt, i) => (
                            <li
                              key={i}
                              style={{
                                color: i === item.answerIndex ? 'var(--correct)' : undefined,
                                fontWeight: i === item.answerIndex ? 600 : undefined,
                              }}
                            >
                              {opt}
                            </li>
                          ))}
                        </ol>
                      ) : null}
                      {item.type !== 'mc' || item.sample ? <ModelAnswer item={item} /> : null}
                    </>
                  )}
                  {item.note ? (
                    <div className="banner banner--info">
                      <strong>Why</strong>
                      <div style={{ whiteSpace: 'pre-wrap' }}>{item.note}</div>
                    </div>
                  ) : null}
                  {item.flag ? <FlagBanner flag={item.flag} text={item.flagText} /> : null}

                  <label className="label" htmlFor={`note-${entry.itemId}`}>
                    Why you missed it
                  </label>
                  <textarea
                    id={`note-${entry.itemId}`}
                    className="input"
                    style={{ minHeight: 72, resize: 'vertical' }}
                    value={draftNote}
                    placeholder="e.g. I keep forgetting to convert to moles first"
                    onChange={(e) => setDraftNote(e.target.value)}
                  />
                  <div className="row">
                    <button
                      className="btn btn--sm btn--primary"
                      onClick={async () => {
                        await setNotebookNote(entry.itemId, draftNote);
                        await reload();
                        toast('Note saved.');
                      }}
                    >
                      Save note
                    </button>
                    <button
                      className="btn btn--sm"
                      onClick={async () => {
                        await setNotebookPinned(entry.itemId, !entry.pinned);
                        await reload();
                        toast(entry.pinned ? 'Unpinned.' : 'Pinned — it will stay here.');
                      }}
                    >
                      {entry.pinned ? 'Unpin' : 'Pin'}
                    </button>
                    <button
                      className="btn btn--sm"
                      onClick={() => navigate('/study', { mode: 'practice', category: item.category, t: String(Date.now()) })}
                    >
                      Practice {item.category}
                    </button>
                  </div>
                </>
              ) : entry.note ? (
                <div className="faint" style={{ whiteSpace: 'pre-wrap' }}>
                  Your note: {entry.note}
                </div>
              ) : null}

              <button
                className="btn btn--sm btn--ghost"
                onClick={() => {
                  setOpenId(isOpen ? null : entry.itemId);
                  setDraftNote(entry.note ?? '');
                }}
              >
                {isOpen ? 'Close' : 'Open'}
              </button>
            </li>
          );
        })}
      </ul>

      {shown.length === 0 ? <p className="muted">Nothing matches that filter.</p> : null}
    </div>
  );
}
